import {
  cn,
  LocationsFilterButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@solinkcorp/solink-ui';
import { Loader2Icon, MapPinIcon } from 'lucide-react';
import { type FC, memo, useState } from 'react';
import useLocale from 'src/hooks/useLocale';
import MultiLocationSelector from './Aspen/MultiLocationSelector';
import SingleLocationSelector from './Aspen/SingleLocationSelector';
import { type LocationSelectorProps } from './LocationSelector';
import SelectedLocations from './SelectedLocations';
import { useLocationSelectorState } from './useLocationSelectorState';

const LocationSelectorAspen: FC<LocationSelectorProps> = (props) => {
  const locale = useLocale();
  const {
    context,
    className = '',
    contentClassName,
    locations,
    multiSelect,
    onLocationSelected,
    onLocationsSelected,
    selectedLocationTags,
    selectedLocationId,
    selectedLocationIds,
    disabled,
  } = props;
  const [isLocationListOpen, setLocationListOpen] = useState(false);

  const {
    masterLocationsList,
    ready,
    fetching,
    hasAccessToLocations,
    locationsWithoutNoCamerasVideoPage,
  } = useLocationSelectorState(locations);

  const closeLocationList = () => setLocationListOpen(false);

  return (
    <Popover open={isLocationListOpen} onOpenChange={setLocationListOpen}>
      <PopoverTrigger asChild>
        <LocationsFilterButton
          className={cn(!hasAccessToLocations && '[&_svg]:invisible', className)}
          disabled={disabled || !ready || !hasAccessToLocations}
          icon={
            ready && fetching ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="relative inline-flex">
                    <MapPinIcon aria-hidden />
                    <Loader2Icon
                      aria-hidden
                      className="absolute -right-1 -top-1 !size-2.5 animate-spin"
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>{locale.locationSelector.fetchingLatest}</TooltipContent>
              </Tooltip>
            ) : !ready ? (
              <Loader2Icon aria-hidden className="animate-spin" />
            ) : undefined
          }
        >
          <SelectedLocations
            locationTags={selectedLocationTags}
            locationIds={
              selectedLocationIds || (selectedLocationId ? [selectedLocationId] : undefined)
            }
            locationsList={masterLocationsList}
            loading={!ready}
            noLocationAccess={!hasAccessToLocations}
            maxCharacterLimit={40}
          />
        </LocationsFilterButton>
      </PopoverTrigger>
      <PopoverContent
        className={cn('!w-auto !p-0', contentClassName)}
        side="bottom"
        align="start"
        collisionPadding={4}
      >
        {multiSelect ? (
          <MultiLocationSelector
            locations={locationsWithoutNoCamerasVideoPage}
            locale={locale}
            selectedLocationIds={selectedLocationIds}
            selectedLocationTags={selectedLocationTags}
            onLocationsSelected={onLocationsSelected}
            closeLocationList={closeLocationList}
            context={context}
            isFetching={fetching}
          />
        ) : (
          <SingleLocationSelector
            locations={masterLocationsList}
            locale={locale}
            selectedLocationId={selectedLocationId}
            onLocationSelected={onLocationSelected}
            closeLocationList={closeLocationList}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default memo(LocationSelectorAspen);
